/**
 * Question helpers for the Drag & Drop sorting stage (old/draganddrop.php).
 * Each question carries its own 2–4 options (option_one..option_four, typed in
 * the admin Quiz tab) and the game shows those as the drop buckets. Rows without
 * their own options fall back to the theme's configured answer categories.
 */

import { ensureThemeJsonArray } from "../../functions/themeAssets";

const OPTION_KEYS = [
  "option_one",
  "option_two",
  "option_three",
  "option_four",
  "option_five",
  "option_six",
];

const MIN_BUCKETS = 2;
export const MAX_BUCKETS = 6;

export function normalizeCategoryName(name) {
  return String(name ?? "").trim().toLowerCase();
}

/** The theme's configured answer categories ({ name, color, image }) — the fixed
 *  bucket set every question drops into. */
export function getDragDropCategories(themeData) {
  return ensureThemeJsonArray(themeData?.categories);
}

/**
 * Normalize one quiz row into { id, question_name, question_image, correctAnswer, options } or
 * null if unusable. When the row has its own options, `options` holds them and
 * `correctAnswer` is one of them; otherwise `options` is empty and
 * `correctAnswer` is a configured category name.
 */
export function normalizeDragDropQuestion(q, categories = []) {
  if (!q || typeof q !== "object") return null;
  // A question is shown either as text or as an image (question_image).
  const question_name = String(q.question_name ?? q.question ?? "").trim();
  const question_image = String(q.question_image ?? "").trim();
  if (!question_name && !question_image) return null;

  const correctRaw = String(q.correct_answer ?? "").trim();
  const categoryByName = new Map(
    (categories || []).map((c) => [normalizeCategoryName(c?.name), String(c?.name ?? "").trim()])
  );

  let correctAnswer = "";
  let options = [];

  // Preferred path: the row's own filled options (resolved by text, then by
  // answer_id position).
  const allOptions = OPTION_KEYS.map((key) => String(q[key] ?? "").trim());
  let filledCount = 0;
  for (const opt of allOptions) {
    if (!opt) break;
    filledCount += 1;
  }

  const rawShow = parseInt(String(q.showOption ?? "").trim(), 10);
  const count =
    Number.isFinite(rawShow) && rawShow >= MIN_BUCKETS
      ? Math.min(MAX_BUCKETS, rawShow)
      : filledCount;
  const optionCount = Math.min(MAX_BUCKETS, count || filledCount);

  if (optionCount >= MIN_BUCKETS) {
    const shapeOptions = allOptions.slice(0, optionCount);
    if (!shapeOptions.some((opt) => !opt)) {
      options = shapeOptions;
      let label = options.find(
        (opt) => normalizeCategoryName(opt) === normalizeCategoryName(correctRaw)
      );
      if (!label) {
        const aid = parseInt(String(q.answer_id ?? "").trim(), 10);
        if (Number.isFinite(aid) && aid >= 1 && aid <= options.length) {
          label = options[aid - 1];
        }
      }
      if (label) {
        correctAnswer = label;
      } else {
        options = [];
      }
    }
  }

  if (!correctAnswer) {
    // Fallback for rows without their own options: correct_answer names a
    // configured category and every category is shown as a bucket.
    correctAnswer = categoryByName.get(normalizeCategoryName(correctRaw)) || "";
  }

  if (!correctAnswer) return null;

  return {
    id: q.id ?? `${question_name || question_image}-${normalizeCategoryName(correctAnswer)}`,
    question_name,
    question_image,
    correctAnswer,
    options,
  };
}

export function isDragDropQuestionPlayable(q, categories) {
  return normalizeDragDropQuestion(q, categories) !== null;
}

/** Theme rows may hold the quiz flat, or as `questions`. */
function readQuizList(themeData) {
  const direct = ensureThemeJsonArray(themeData?.quiz);
  if (direct.length) return direct;
  return ensureThemeJsonArray(themeData?.questions);
}

/** All playable drag & drop statements configured on the theme. */
export function getDragDropQuestions(themeData) {
  const categories = getDragDropCategories(themeData);
  return readQuizList(themeData)
    .map((q) => normalizeDragDropQuestion(q, categories))
    .filter(Boolean);
}

export function shuffleCopy(arr) {
  const next = arr.slice();
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const t = next[i];
    next[i] = next[j];
    next[j] = t;
  }
  return next;
}

export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
